import { InvitationsFilterModel } from '@routes/events/models/invitationlist-model';
import { EventMailType } from '@shared/enums/EventMailType';
import { IntegrationType } from '@shared/enums/IntegrationType';
import { InvitationStatusType } from '@shared/enums/InvitationStatusType';
import { IChargeModel } from '@shared/models/charge.model';
import { IContactModel } from '@shared/models/contact.model';
import { IUserModel } from '@shared/models/user.model';
import { IVenueModel } from '@shared/models/venue.model';
import { ActivityTimeRange, IActivityLogBase } from '@shared/services/activity-service/activity.models';
import { ContactType } from '@shared/enums/ContactType';
import { CustomContactFieldType } from '@shared/enums/CustomContactFieldType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { IFieldTranslations } from '@shared/models/tag.model';
import { ContactActionType } from '@shared/enums/ContactActionType';
import { ContactActionByType } from '@shared/enums/ContactActionByType';
import { ConnectorType } from '@shared/enums/ConnectorType';

export interface IInvitationDetail {
    id: string;
    index: number;
    maxGuests: number;
    customIdentifier: string;
    receivedSaveDate: boolean;
    receivedTicket: boolean;
    receivedConfirmation: boolean;
    receivedPostEvent: boolean;
    receivedPreEvent: boolean;
    receivedReminder: boolean;
    receivedInvitation: boolean;
    invitationDate: Date;
    confirmationDate: Date;
    declinationDate: Date;
    reservationDate: Date;
    rsvpDate: Date;
    statusType: InvitationStatusType;
    contact: IContactModel;
    ticketHolder: IUserModel;
    locationDateTime: IVenueModel;
    charges: IChargeModel[];
    invitationGroups: [];
    languageId: string;
}

export interface IDeclineInvitation {
    ids: string[];
    isSelectAll: boolean;
    filterModel: InvitationsFilterModel;
}

export class DeclineInvitation implements IDeclineInvitation {
    constructor(
        public ids: string[],
        public isSelectAll: boolean,
        public filterModel: InvitationsFilterModel
    ) { }
}

export class SyncInvitationsToCrm {
    constructor(
        public ids: string[],
        public isSelectAll: boolean,
        public filterModel: InvitationsFilterModel,
        public crm: IntegrationType
    ) { }
}

export class SyncInvitationsToInviteDesk {
    constructor(
        public eventId: string,
        public crm: IntegrationType
    ) { }
}

export class ActivityFilter {
    constructor(public timeRange: ActivityTimeRange, public category: ActivityCategory = null, public userId: string = null, public contactId= null) { }
  }


export enum ActivityCategory {
    Created = 0,
    Declined = 1,
    Updated = 2,
    Confirmed = 3,
    Batch = 4,
    Deleted = 5,
    Mail = 6,
    Export = 8
}

export interface IActivityLog  extends IActivityLogBase {
    eventName: string;
    isPublicPage: boolean;
    isContactCreated: boolean;
    isUpdate: boolean;
    crm: string;
    mailType: EventMailType;
}
export interface IActivities {
    activities: IActivityLog[];
}
export interface IActivityLogs {
    activities: IActivityLog[];
}

export interface IInvitationSyncCrmBatchActivityLog  extends IActivityLog {
    array: SyncedInvitation[];
}

export interface IInvitationImportedInBatchConnectorActivity  extends IActivityLog {
    batchCount: number;
    okCount: number;
    failCount: number;
}

export class SyncedInvitation {
    /**
     * OK or FAIL
     */
    result: string;
    invitationId: string;
    contactId: string;
}

export class IAdvancedSortSettings {
    declineByPublicListQueryId: string;
    declineWaitingInvitationsOption: string;
    priorityInvitationsQueryId: string;
    reserveInvitationsQueryId: string;
    sortByField: string;
    sortWaitingInvitations: string;
}

export interface IAddContact {
    ticketHolderId?: string;
    eventId?: string;
    firstName: string;
    lastName: string;
    title: string;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    accountOwnerId: string;
    crmId: string;
    customerNumber: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    type: ContactType;
    contactCustomFields?: IContactCustomField[];
    groupIds: string[];
    contactTags?: IContactTag[];
    unsubscribes?: IContactUnsubscribe[];
    logs?: IContactLog[];
}

export interface IContactLog {
    id: string;
    created: Date;
    unsubscribeOption: IContactUnsubscribeOption;
    user: IContactUnsubscribeUser;
    actionType: ContactActionType;
    actionByType: ContactActionByType;
    connectorType: ConnectorType;
}

export interface IContactUnsubscribeOption {
    id: string;
    name: string;
}

export interface IContactUnsubscribeUser {
    firstName: string;
    lastName: string;
    email: string;
}

export interface IContactCustomField {
    id: string;
    index: number;
    booleanValue: boolean;
    textValue: string;
    numberValue: number;
    dateTimeValue: Date;
    customFieldType: CustomContactFieldType;
    freeTextFormat: FreeTextFormat;
    translations: IFieldTranslations[];
    availableCustomFieldOptions: ICustomContactFieldOption[];
    contactCustomFieldOptions: ICustomContactFieldOption[];
    contactCustomFieldUsers: ICustomContactFieldUser[];
}

export interface ICustomContactFieldUser {
    userId: string;
}

export interface ICustomContactFieldOption {
    id: string;
    index: number;
    translations: IFieldTranslations[];
}

export interface IContactTag {
    id: string;
    index: number;
    translations: IFieldTranslations[];
}

export interface IContactUnsubscribe {
    unsubscribeDate: Date;
    unsubscribeOptionId: string;
}

export class AddContactInvitation implements IAddContact {
    constructor(
        public ticketHolderId: string,
        public firstName: string,
        public lastNamePrefix: string,
        public lastName: string,
        public title: string,
        public type: ContactType,
        public genderId: string,
        public languageId: string,
        public email: string,
        public phone: string,
        public mobile: string,
        public companyName: string,
        public salutation: string,
        public mailPermission: boolean,
        public smsPermission: boolean,
        public accountOwnerId: string,
        public addressLineOne: string,
        public addressLineTwo: string,
        public city: string,
        public postalCode: string,
        public country: string,
        public crmId: string,
        public customerNumber: string,
        public groupIds: string[],
        public contactCustomFields: IContactCustomField[],
        public contactTags?: IContactTag[],
        public unsubscribes?: IContactUnsubscribe[],
        public logs?: IContactLog[]
    ) { }
}
